// Pure finance-tab derivation logic — the heart of the Finance Tab feature.
// Given the stored entities (purchase orders, invoices, finance setup) it
// derives every number and validation the UI shows: per-line balances, the
// in-context before/after balances on a specific invoice, pending contention
// between review invoices, the PO position rollup, the contract-level PO
// rollup, the record-modal payable picker, the approval-email body, an
// unapproved invoice's due-soon/overdue aging, an unapproved invoice's
// approval-due-date deadline, and a standalone pre-approval payment table
// sharing the email's row/table formatting.
//
// This is the frontend MIRROR of `backend/src/models/financeCalc.ts` (the
// canonical implementation). Same exported names, same logic, same output
// strings. The shared JSON fixtures under `tests/shared/financeCalcFixtures/`
// are the binding contract between the two — any change to a derivation in
// either stack MUST update the relevant fixture and keep BOTH parity tests
// green (`backend/tests/models/financeCalc.test.ts` and
// `frontend/src/utils/financeCalc.parity.test.ts`). See that directory's
// README and the FE<->BE parity precedent in ADR-015
// (docs/project_notes/decisions.md).
//
// Entity types (PurchaseOrder, Invoice, PoLine, RateItem, ...) are imported
// from ../types/finance, the FE mirror of backend/src/models/finance.ts —
// same relationship as the backend importing from ./finance.js. The
// calc-only types declared in this file (LineBalance, ValidationResult,
// Payables and friends, ContractRollup, MilestoneRef/SowRef) are re-exported
// from ../types/finance so callers have one place to import from.
//
// Deliberately pure and dependency-light: no I/O, no clock.
//
// Line consumption ("consumed" in LineBalance, and the "before" balance in
// InvoiceDerivation) is always the sum of approved-invoice splits PLUS the
// line's own `adjustments` ledger (opening balances, SAP true-ups). Unlike
// invoice splits, adjustments are not tied to any invoice, so they are never
// invoice-scoped — they count identically no matter which invoice (if any)
// is being derived.
//
// The record-modal payable picker (derivePayables) is tag-driven: a PO
// line's rateItemIds/milestoneIds/sowIds arrays are the source of truth for
// which lines a milestone/product/SOW payable suggests, rather than a
// separate setup mapping table.
//
// Money epsilons (all in dollars): a $0.005 tolerance guards balance
// overdraw/allocation comparisons (half a cent — below the smallest real
// money movement) and a $0.01 tolerance guards price-match comparisons.

import type { PurchaseOrder, Invoice, PoLine, RateItem } from "../types/finance";

// ── money formatting ──────────────────────────────────────────────────────

// Single source of truth for dollar display so the backend approval email and
// the frontend preview render byte-identical figures. e.g. 1234.5 → "$1,234.50".
export function formatAud(n: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

// ── validation primitives ──────────────────────────────────────────────────

export type ValidationKind = "ok" | "warn" | "over" | "under" | "none";
export interface ValidationResult {
  kind: ValidationKind;
  text: string;
}

// A line is "near limit" once its remaining budget drops to within the larger
// of $100 or 1% of the line's total — a flat floor so tiny lines still warn,
// scaling up on large lines.
function nearLimitThreshold(lineTotal: number): number {
  return Math.max(100, lineTotal * 0.01);
}

// Axis 1 — does allocating `amount` against this line keep it within budget?
// `after` is the balance the line would have once `amount` is drawn down.
export function validateLineBalance(
  line: { remaining: number; lineTotal: number },
  amount: number
): ValidationResult {
  const after = line.remaining - amount;
  if (after < -0.005) {
    return { kind: "over", text: `Overdrawn ${formatAud(-after)}` };
  }
  if (after <= nearLimitThreshold(line.lineTotal)) {
    return { kind: "warn", text: `Line near limit · ${formatAud(after)} left` };
  }
  return { kind: "ok", text: "Balance ok" };
}

// Axis 2 — does the charge's total match the agreed price? A null/undefined
// expected amount means the price is variable/contingency: no signal to give.
export function validatePrice(
  chargeSum: number,
  expectedAmount: number | null | undefined
): ValidationResult {
  if (expectedAmount === null || expectedAmount === undefined) {
    return { kind: "none", text: "" };
  }
  const variance = chargeSum - expectedAmount;
  if (Math.abs(variance) <= 0.01) {
    return { kind: "ok", text: "Matches price" };
  }
  if (variance > 0) {
    return { kind: "over", text: `Overpaying ${formatAud(variance)}` };
  }
  return { kind: "under", text: `Under ${formatAud(-variance)} vs price` };
}

// Worst-of ranking used to collapse every balance and price signal on an
// invoice into a single pill. ok/none carry no signal (rank 0); the display is
// exceptions-only, so an all-clear invoice returns null (no pill).
const VALIDATION_RANK: Record<ValidationKind, number> = {
  ok: 0,
  none: 0,
  warn: 1,
  under: 2,
  over: 3,
};

type AggregateKind = "warn" | "under" | "over";
const AGGREGATE_LABEL: Record<AggregateKind, string> = {
  warn: "Line near limit",
  under: "Under expected",
  over: "Needs attention",
};

export function aggregateValidation(
  results: ValidationResult[]
): { kind: AggregateKind; label: string } | null {
  let worst = 0;
  for (const r of results) {
    const rank = VALIDATION_RANK[r.kind];
    if (rank > worst) worst = rank;
  }
  if (worst <= 0) return null;
  const kind: AggregateKind = worst === 3 ? "over" : worst === 2 ? "under" : "warn";
  return { kind, label: AGGREGATE_LABEL[kind] };
}

// ── per-PO-line balances ───────────────────────────────────────────────────

export interface LineBalance {
  lineTotal: number;
  consumed: number;
  remaining: number;
  nearLimit: boolean;
  overdrawn: boolean;
}

// Only approved invoices consume budget; review and queried invoices are
// pending and consume nothing. Every line of the PO gets an entry (invoiced or
// not); splits pointing at line ids the PO does not have are ignored. A
// line's own `adjustments` ledger (opening balances, SAP true-ups) is always
// counted — those entries are not tied to any invoice, so they apply
// unconditionally rather than being invoice-scoped like approved splits.
//
// No production caller: the UI renders backend-attached derived fields
// instead of recomputing this locally. This mirror exists to uphold the
// shared-fixture FE↔BE parity contract (tests/shared/financeCalcFixtures/)
// — do not delete it.
export function computeLineBalances(
  po: PurchaseOrder,
  invoices: Invoice[]
): Record<string, LineBalance> {
  const onPo = invoices.filter((inv) => inv.poId === po.id);
  const lineIds = new Set(po.lines.map((l) => l.id));
  const consumedByLine: Record<string, number> = {};
  for (const inv of onPo) {
    if (inv.status !== "approved") continue;
    for (const charge of inv.charges) {
      for (const split of charge.splits) {
        if (!lineIds.has(split.poLineId)) continue;
        consumedByLine[split.poLineId] = (consumedByLine[split.poLineId] ?? 0) + split.amount;
      }
    }
  }
  const out: Record<string, LineBalance> = {};
  for (const line of po.lines) {
    const adjustments = line.adjustments.reduce((acc, a) => acc + a.amount, 0);
    const consumed = (consumedByLine[line.id] ?? 0) + adjustments;
    const remaining = line.lineTotal - consumed;
    const overdrawn = remaining < -0.005;
    const nearLimit = !overdrawn && remaining <= nearLimitThreshold(line.lineTotal);
    out[line.id] = { lineTotal: line.lineTotal, consumed, remaining, nearLimit, overdrawn };
  }
  return out;
}

// ── in-context invoice derivation ───────────────────────────────────────────

export interface DerivedSplit {
  poLineId: string;
  amount: number;
  remainingBefore: number;
  remainingAfter: number;
  balance: ValidationResult;
  pendingElsewhere: number;
  contendingInvoiceNumbers: string[];
  jointOverdraw: boolean;
}

export interface DerivedCharge {
  chargeId: string;
  price: ValidationResult;
  splits: DerivedSplit[];
}

export interface InvoiceDerivation {
  charges: DerivedCharge[];
  allocatedTotal: number;
  balanced: boolean;
  pill: ReturnType<typeof aggregateValidation>;
}

// Every balance shown ON an invoice is the after-this-invoice balance. The
// "before" excludes this invoice's own splits from approved consumption: for a
// review invoice that equals the line's live remaining, and for an already
// approved invoice it backs its own splits out so they are not subtracted
// twice — an approved invoice's split shows the same before/after it showed at
// approval time.
export function deriveInvoice(
  po: PurchaseOrder,
  invoices: Invoice[],
  invoiceId: string
): InvoiceDerivation {
  const onPo = invoices.filter((inv) => inv.poId === po.id);
  const target = onPo.find((inv) => inv.id === invoiceId);
  const lineById = new Map<string, PoLine>(po.lines.map((l) => [l.id, l]));

  // Approved consumption on a line, excluding the invoice being derived, plus
  // the line's own adjustments ledger — adjustments are never invoice-scoped,
  // so they always apply to the "before" balance regardless of which invoice
  // is being derived.
  const consumedExcluding = (poLineId: string): number => {
    const line = lineById.get(poLineId);
    let sum = line ? line.adjustments.reduce((acc, a) => acc + a.amount, 0) : 0;
    for (const inv of onPo) {
      if (inv.status !== "approved" || inv.id === invoiceId) continue;
      for (const charge of inv.charges) {
        for (const split of charge.splits) {
          if (split.poLineId === poLineId) sum += split.amount;
        }
      }
    }
    return sum;
  };

  // Amounts other review invoices have pending against the same line, plus the
  // (deduplicated, in-order) invoice numbers contending for it. Approved and
  // queried invoices, and this invoice's own splits, are excluded.
  const pendingElsewhereFor = (poLineId: string): { sum: number; numbers: string[] } => {
    let sum = 0;
    const numbers: string[] = [];
    for (const inv of onPo) {
      if (inv.status !== "review" || inv.id === invoiceId) continue;
      let hit = false;
      for (const charge of inv.charges) {
        for (const split of charge.splits) {
          if (split.poLineId === poLineId) {
            sum += split.amount;
            hit = true;
          }
        }
      }
      if (hit && !numbers.includes(inv.invoiceNumber)) numbers.push(inv.invoiceNumber);
    }
    return { sum, numbers };
  };

  const allResults: ValidationResult[] = [];
  const charges: DerivedCharge[] = [];
  let allocatedTotal = 0;

  // Running total of THIS invoice's own draw per line, accumulated in split
  // order across every charge. A split's "before" balance nets off earlier
  // same-line splits in this invoice, so two splits that overdraw a single line
  // between them are caught by the second split's balance (and the gate) rather
  // than each independently measuring against the full pre-invoice remaining.
  const drawnThisInvoice = new Map<string, number>();

  for (const charge of target?.charges ?? []) {
    const chargeSum = charge.splits.reduce((acc, s) => acc + s.amount, 0);
    const price = validatePrice(chargeSum, charge.expectedAmount);
    allResults.push(price);

    const splits: DerivedSplit[] = [];
    for (const split of charge.splits) {
      const lineTotal = lineById.get(split.poLineId)?.lineTotal ?? 0;
      const priorThisInvoice = drawnThisInvoice.get(split.poLineId) ?? 0;
      const remainingBefore = lineTotal - consumedExcluding(split.poLineId) - priorThisInvoice;
      const remainingAfter = remainingBefore - split.amount;
      const balance = validateLineBalance({ remaining: remainingBefore, lineTotal }, split.amount);
      allResults.push(balance);

      const pending = pendingElsewhereFor(split.poLineId);
      const jointOverdraw = remainingAfter - pending.sum < -0.005;
      allocatedTotal += split.amount;
      drawnThisInvoice.set(split.poLineId, priorThisInvoice + split.amount);

      splits.push({
        poLineId: split.poLineId,
        amount: split.amount,
        remainingBefore,
        remainingAfter,
        balance,
        pendingElsewhere: pending.sum,
        contendingInvoiceNumbers: pending.numbers,
        jointOverdraw,
      });
    }
    charges.push({ chargeId: charge.id, price, splits });
  }

  const balanced = Math.abs(allocatedTotal - (target?.totalAmount ?? 0)) <= 0.005;
  const pill = aggregateValidation(allResults);
  return { charges, allocatedTotal, balanced, pill };
}

// ── PO position rollup ──────────────────────────────────────────────────────

export interface PoPosition {
  limit: number;
  approved: number;
  inReview: number;
  remaining: number;
}

// Queried invoices are excluded from both approved and in-review totals.
//
// No production caller: the UI renders backend-attached derived fields
// instead of recomputing this locally. This mirror exists to uphold the
// shared-fixture FE↔BE parity contract (tests/shared/financeCalcFixtures/)
// — do not delete it.
export function computePoPosition(po: PurchaseOrder, invoices: Invoice[]): PoPosition {
  const onPo = invoices.filter((inv) => inv.poId === po.id);
  let approved = 0;
  let inReview = 0;
  for (const inv of onPo) {
    if (inv.status === "approved") approved += inv.totalAmount;
    else if (inv.status === "review") inReview += inv.totalAmount;
  }
  const limit = po.totalValue;
  return { limit, approved, inReview, remaining: limit - approved - inReview };
}

// ── payable derivation (for the record-invoice modal picker) ────────────────

// Structural refs so the frontend mirror can pass its own milestone/SOW shapes
// without importing tracking models.
export interface MilestoneRef {
  id: string;
  name: string;
  paymentAmount?: number;
  sowId?: string | null;
  /** Milestone register number; when present the payable label is prefixed
   *  "M{number}{suffix} · " so the operator can match invoice lines by the
   *  number they know, not just the name. */
  milestoneNumber?: number;
  /** Optional single-letter insertion suffix; number 13 + "a" reads "13a". */
  milestoneSuffix?: string;
}
export interface SowRef {
  id: string;
  number?: string;
  name: string;
}

export interface MilestonePayable {
  payableId: string;
  milestoneId: string;
  label: string;
  expected: number | null;
  suggestedLineIds: string[];
}
export interface ProductPayable {
  payableId: string;
  rateItemId: string;
  label: string;
  rate: number | null;
  unit: string;
  gstApplies: boolean;
  suggestedLineIds: string[];
}
export interface SowPayable {
  payableId: string;
  sowId: string;
  label: string;
  suggestedLineIds: string[];
}
export interface OtherPayable {
  payableId: string;
  label: string;
  suggestedLineId: string;
}
export interface Payables {
  milestones: MilestonePayable[];
  products: ProductPayable[];
  sows: SowPayable[];
  other: OtherPayable[];
}

// Every PO line whose tag array (rateItemIds/milestoneIds/sowIds) contains
// the given id, in PO-line order.
const linesTaggedWith = (
  poLines: PoLine[],
  id: string,
  key: "rateItemIds" | "milestoneIds" | "sowIds"
): string[] => poLines.filter((l) => l[key].includes(id)).map((l) => l.id);

// Groups the things an invoice charge can be raised against, tag-driven off
// the PO lines themselves (rather than a separate setup mapping table):
//  - milestones: every live tracking milestone, regardless of whether any PO
//    line currently tags it (an untagged milestone is still pickable — the
//    tag just seeds the suggested lines). paymentAmount is read live off the
//    milestone, so `expected` is null when the milestone has no amount.
//  - products: every rate-card item, with every PO line tagging it suggested.
//  - sows: every SOW, with every PO line tagging it suggested.
//  - other: one entry per contingency/ad-hoc PO line (balance-only, no
//    price) — these lines carry no rate-card/milestone/SOW tag by design.
export function derivePayables(
  rateCard: RateItem[],
  milestones: MilestoneRef[],
  sows: SowRef[],
  poLines: PoLine[]
): Payables {
  const sowById = new Map<string, SowRef>(sows.map((s) => [s.id, s]));

  const milestonesOut: MilestonePayable[] = milestones.map((m) => {
    let label =
      m.milestoneNumber !== undefined
        ? `M${m.milestoneNumber}${m.milestoneSuffix ?? ""} · ${m.name}`
        : m.name;
    if (m.sowId) {
      const sow = sowById.get(m.sowId);
      if (sow) label = `${sow.number ? sow.number : sow.name} · ${label}`;
    }
    return {
      payableId: `milestone:${m.id}`,
      milestoneId: m.id,
      label,
      expected: m.paymentAmount ?? null,
      suggestedLineIds: linesTaggedWith(poLines, m.id, "milestoneIds"),
    };
  });

  const productsOut: ProductPayable[] = rateCard.map((r) => ({
    payableId: `rate:${r.id}`,
    rateItemId: r.id,
    label: r.item,
    rate: r.unitPrice,
    unit: r.unit,
    gstApplies: r.gstApplies,
    suggestedLineIds: linesTaggedWith(poLines, r.id, "rateItemIds"),
  }));

  const sowsOut: SowPayable[] = sows.map((s) => ({
    payableId: `sow:${s.id}`,
    sowId: s.id,
    label: s.number ? s.number : s.name,
    suggestedLineIds: linesTaggedWith(poLines, s.id, "sowIds"),
  }));

  const otherOut: OtherPayable[] = poLines
    .filter((l) => l.lineType === "contingency" || l.lineType === "other")
    .map((l) => ({
      payableId: `line:${l.id}`,
      label: `${l.lineType === "contingency" ? "Contingency" : "Ad-hoc"} — ${l.description}`,
      suggestedLineId: l.id,
    }));

  return { milestones: milestonesOut, products: productsOut, sows: sowsOut, other: otherOut };
}

// ── contract-level PO rollup ─────────────────────────────────────────────

export interface ContractRollup {
  committed: number;
  contractValue: number;
  remaining: number;
  overCommitted: boolean;
}

// Sums every PO's approved limit (regardless of open/closed status — a
// closed PO still committed its value) against the contract's headline
// value, so the Finance tab can flag when POs collectively exceed contract.
export function computeContractRollup(
  purchaseOrders: PurchaseOrder[],
  contractValue: number
): ContractRollup {
  const committed = purchaseOrders.reduce((acc, po) => acc + po.totalValue, 0);
  const remaining = contractValue - committed;
  return { committed, contractValue, remaining, overCommitted: committed - contractValue > 0.005 };
}

// ── approval email ──────────────────────────────────────────────────────────

/** Row-quantity display: non-zero values below 0.01 floor to "<0.01" (a
 *  fixed precision would still collapse somewhere); otherwise 2 dp with
 *  trailing zeros trimmed. */
export function formatQty(qty: number): string {
  if (qty > 0 && qty < 0.01) return "<0.01";
  return String(Math.round(qty * 100) / 100);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Structural refs so the frontend mirror and the e2e stub can pass their own
// shapes without importing the model types.
export interface ApprovalEmailLineRef {
  id: string;
  lineNumber: string;
  description: string;
}
export interface ApprovalEmailChargeRef {
  qty?: number;
  expectedAmount?: number | null;
  splits: Array<{ poLineId: string; amount: number; qty?: number }>;
}
export interface ApprovalEmailSplit {
  amount: number;
  lineNumber: string;
  item: string;
  qty: number;
}

/** Assemble the email's rows: line lookup plus the row-quantity precedence.
 *  Precedence: the split's entered qty; a blank box on a multi-split rated
 *  charge derives amount ÷ unit rate (the charge-level total is never a row
 *  value there); a single-split rated charge uses the charge qty; a charge
 *  with only an agreed amount derives amount ÷ expected; otherwise 1.
 *  Derived values are returned unrounded — formatQty owns display. */
export function approvalEmailSplits(
  lines: ApprovalEmailLineRef[],
  charges: ApprovalEmailChargeRef[]
): ApprovalEmailSplit[] {
  const lineById = new Map(lines.map((l) => [l.id, l]));
  const out: ApprovalEmailSplit[] = [];
  for (const charge of charges) {
    const multiSplit = charge.splits.length > 1;
    const expected = charge.expectedAmount ?? null;
    for (const split of charge.splits) {
      let qty: number;
      if (split.qty !== undefined) {
        qty = split.qty;
      } else if (multiSplit && charge.qty !== undefined && expected !== null && expected > 0) {
        qty = split.amount / (expected / charge.qty);
      } else if (!multiSplit && charge.qty !== undefined) {
        qty = charge.qty;
      } else if (expected !== null && expected > 0) {
        qty = split.amount / expected;
      } else {
        qty = 1;
      }
      const line = lineById.get(split.poLineId);
      out.push({
        amount: split.amount,
        lineNumber: line?.lineNumber ?? split.poLineId,
        item: line?.description ?? "",
        qty,
      });
    }
  }
  return out;
}

export interface ApprovalEmailInput {
  invoiceNumber: string;
  poNumber: string;
  contractNumber: string;
  managerName: string;
  financeEmail: string;
  totalAmount: number;
  splits: ApprovalEmailSplit[];
}

const CELL = "border:1px solid #000000;padding:2pt 6pt;";
const FONT = "font-family:Aptos,Calibri,Helvetica,Arial,sans-serif;font-size:11pt;color:#000000";

// Outlook's paste strips CSS margins (photographed in the field), so block
// spacing is explicit spacer paragraphs — literal structure survives every
// paste mode. Content paragraphs carry margin:0 and rely on the spacers.
const SPACER = `<p style="margin:0">&nbsp;</p>`;

// One source for payment-row formatting, shared by the approval email and the
// standalone pre-approval payment table. The two surfaces differ only in
// framing (intro/sign-off, total label) — the bullet rows and the <table>
// fragment must stay byte-identical so a table circulated for review matches
// the eventual approval email exactly.
function paymentRowsText(splits: ApprovalEmailSplit[]): string[] {
  return splits.map(
    (s) => `• ${s.lineNumber} - ${s.item} · qty ${formatQty(s.qty)} · ${formatAud(s.amount)} ex GST`
  );
}

function paymentTableHtml(splits: ApprovalEmailSplit[], totalAmount: number): string {
  const rows = splits
    .map(
      (s) =>
        `<tr><td style="${CELL}">${escapeHtml(`${s.lineNumber} - ${s.item}`)}</td>` +
        `<td style="${CELL}">${escapeHtml(formatQty(s.qty))}</td>` +
        `<td align="right" style="${CELL}">${formatAud(s.amount)}</td></tr>`
    )
    .join("");
  return (
    `<table style="border-collapse:collapse">` +
    `<tr><th align="left" style="${CELL}font-weight:bold">Line item</th>` +
    `<th align="left" style="${CELL}font-weight:bold">Qty</th>` +
    `<th align="right" style="${CELL}font-weight:bold">Amount (ex GST)</th></tr>` +
    rows +
    `<tr><td colspan="2" align="right" style="${CELL}font-weight:bold">Total</td>` +
    `<td align="right" style="${CELL}font-weight:bold">${formatAud(totalAmount)}</td></tr>` +
    `</table>`
  );
}

// Two flavours of the same content: a plain-text body (clipboard fallback,
// SES text part) and an inline-styled HTML body whose table pastes into
// Outlook as a real table. Byte-identical FE<->BE via the parity fixtures.
// Every interpolated value in the HTML is escaped — line descriptions and
// the approver name are user-entered text.
export function buildApprovalEmail(input: ApprovalEmailInput): {
  to: string;
  subject: string;
  body: string;
  bodyHtml: string;
} {
  const {
    invoiceNumber,
    poNumber,
    contractNumber,
    managerName,
    financeEmail,
    totalAmount,
    splits,
  } = input;

  const intro = `The attached invoice ${invoiceNumber} for contract ${contractNumber} has been approved for payment as follows:`;
  const body = [
    "Hi,",
    "",
    intro,
    "",
    `PO: ${poNumber}`,
    "",
    ...paymentRowsText(splits),
    "",
    `Total approved: ${formatAud(totalAmount)} ex GST`,
    "",
    `Approved by ${managerName}.`,
  ].join("\n");

  const bodyHtml =
    `<div style="${FONT}">` +
    `<p style="margin:0">Hi,</p>` +
    SPACER +
    `<p style="margin:0">${escapeHtml(intro)}</p>` +
    SPACER +
    `<p style="margin:0">PO: ${escapeHtml(poNumber)}</p>` +
    SPACER +
    paymentTableHtml(splits, totalAmount) +
    SPACER +
    `<p style="margin:0">${escapeHtml(`Approved by ${managerName}.`)}</p>` +
    `</div>`;

  return {
    to: financeEmail,
    subject: `Approved for payment — Invoice ${invoiceNumber} — ${contractNumber}`,
    body,
    bodyHtml,
  };
}

/** The pre-approval payment table: the same PO line, bullet rows / HTML
 *  <table> the approval email will contain, without approval framing — the
 *  total line is "Total:" (nothing is approved yet) and there is no
 *  intro/sign-off. Circulated by managers for review before approving. */
export interface PaymentTableInput {
  poNumber: string;
  totalAmount: number;
  splits: ApprovalEmailSplit[];
}

export function buildPaymentTable(input: PaymentTableInput): { text: string; html: string } {
  const { poNumber, totalAmount, splits } = input;
  const text = [
    `PO: ${poNumber}`,
    "",
    ...paymentRowsText(splits),
    "",
    `Total: ${formatAud(totalAmount)} ex GST`,
  ].join("\n");
  const html =
    `<div style="${FONT}">` +
    `<p style="margin:0">PO: ${escapeHtml(poNumber)}</p>` +
    SPACER +
    paymentTableHtml(splits, totalAmount) +
    `</div>`;
  return { text, html };
}

// ─── Invoice aging ───────────────────────────────────────────────────────
// Time-dependent derived state (never stored): an unapproved invoice is
// "due soon" once 20 calendar days old and "overdue" at 30, counted from
// invoiceDate. `today` is a YYYY-MM-DD string supplied by the caller
// (Brisbane date at runtime call sites) so this stays pure and portable.

export type InvoiceAging = "none" | "due-soon" | "overdue";

export const INVOICE_DUE_SOON_AGE_DAYS = 20;
export const INVOICE_OVERDUE_AGE_DAYS = 30;

// Local calendar-date parse that REJECTS rolled dates (2026-02-31): the
// stored schema is regex-only, so a real-calendar check must happen here
// for the derivation to fail safe rather than silently mis-age.
function parseUtcMidnight(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const ms = Date.UTC(year, month - 1, day);
  const rebuilt = new Date(ms);
  if (
    rebuilt.getUTCFullYear() !== year ||
    rebuilt.getUTCMonth() + 1 !== month ||
    rebuilt.getUTCDate() !== day
  ) {
    return null;
  }
  return ms;
}

/** Calendar days from `base` to `target`; null when either is invalid. */
export function invoiceAgeDays(base: string, target: string): number | null {
  const b = parseUtcMidnight(base);
  const t = parseUtcMidnight(target);
  if (b === null || t === null) return null;
  return Math.round((t - b) / 86_400_000);
}

export function deriveInvoiceAging(
  invoice: Pick<Invoice, "status" | "invoiceDate">,
  today: string
): InvoiceAging {
  if (invoice.status === "approved") return "none";
  const age = invoiceAgeDays(invoice.invoiceDate, today);
  if (age === null) return "none";
  if (age >= INVOICE_OVERDUE_AGE_DAYS) return "overdue";
  if (age >= INVOICE_DUE_SOON_AGE_DAYS) return "due-soon";
  return "none";
}

/** Approval deadline: invoiceDate + INVOICE_OVERDUE_AGE_DAYS — the date the
 *  invoice-overdue alert fires and the queue's "Approval due" column shows.
 *  Pure date math with no status input (display policy is the caller's
 *  concern); null when the stored date is not a real calendar date. */
export function invoiceApprovalDueDate(invoice: Pick<Invoice, "invoiceDate">): string | null {
  const ms = parseUtcMidnight(invoice.invoiceDate);
  if (ms === null) return null;
  return new Date(ms + INVOICE_OVERDUE_AGE_DAYS * 86_400_000).toISOString().slice(0, 10);
}
